import type { ComponentProps } from 'react';
import { HiddenRadioInput } from '@/ui-kit/components/radios/shared';
import { cn } from '@/ui-kit/utils';

type RadioGroupItemProps = Omit<ComponentProps<'input'>, 'type' | 'aria-invalid'> & {
  error?: boolean;
  value: string | number;
  children: string;
};

export const RadioGroupItem = ({
  error = false,
  className,
  children,
  disabled,
  ...restProps
}: RadioGroupItemProps) => (
  <label className={cn('flex items-center gap-2', disabled ? 'cursor-default' : 'cursor-pointer', className)}>
    <HiddenRadioInput aria-invalid={error} disabled={disabled} {...restProps} />
    <span
      aria-hidden="true"
      className={cn(
        'border-icon-subtle bg-surface-default m-0.75 flex size-4.5 shrink-0 items-center justify-center rounded-full border-2',
        'transition-[border-color] duration-200 ease-out',
        'before:bg-icon-subtle before:size-2.5 before:scale-0 before:rounded-full before:transition-all before:duration-200 before:ease-out before:content-[""]',
        'peer-checked:border-icon-brand peer-checked:before:bg-icon-brand peer-checked:before:scale-100',
        'peer-aria-invalid:border-icon-error peer-aria-invalid:before:bg-icon-error',
        'peer-disabled:border-icon-disabled peer-disabled:before:bg-icon-disabled',
        'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2',
      )}
    />
    <span className={cn('peer-disabled:text-text-disabled')}>{children}</span>
  </label>
);

type RadioGroupProps = Omit<ComponentProps<'div'>, 'role'>;

export const RadioGroup = (props: RadioGroupProps) => <div role="radiogroup" {...props} />;
